import {
    ParameterGroup,
    ParameterGroupValue,
} from 'communication/types/strategyParams/StrategyParametersElement';
import { TimeFormat } from '@instinet-analytics/core-lib';
import { StrategyParametersStreamDataState } from '../store/stream/StrategyParametersStreamState';

export interface StrategyParametersRowData {
    name: string;
    val: ParameterGroupValue;
    newValue?: boolean;
}

const mapParameterGroupToRowData = (
    pg: ParameterGroup
): StrategyParametersRowData[] =>
    Object.keys(pg).map((key) => ({ name: key, val: pg[key] }));

export function computeGridData(
    streamDataState: StrategyParametersStreamDataState | undefined,
    timeFormat: TimeFormat
): StrategyParametersRowData[][] {
    if (!streamDataState || !streamDataState.orderSelection) {
        return [[]];
    }

    const { orderSelection, paramGroups } = streamDataState;
    const result: StrategyParametersRowData[][] =
        orderSelection === 'multiple'
            ? [mapParameterGroupToRowData(paramGroups[0])]
            : computeDisplayPages(paramGroups);

    return result.map((arr) =>
        arr.map((x) => ({
            ...x,
            name: replaceUnderscores(x.name),
            val: formatValue(x, timeFormat),
        }))
    );
}

function computeDisplayPages(
    paramGroups: ParameterGroup[]
): StrategyParametersRowData[][] {
    const result: StrategyParametersRowData[][] = [];

    let cumulativePage: StrategyParametersRowData[] = [];
    for (let x = 0; x < paramGroups.length; ++x) {
        const currentPage = paramGroups[x];
        const currentPageIds = Object.keys(currentPage);

        // remove incoming rows from current results list
        const filteredResults = cumulativePage.filter(
            (x) => !currentPageIds.includes(x.name)
        );

        cumulativePage = [
            ...mapParameterGroupToRowData(currentPage).map((x) => ({
                ...x,
                newValue: true,
            })),
            ...filteredResults,
        ];

        result.push(cumulativePage);

        // clear previous new/updated rows
        cumulativePage = cumulativePage.map((x) => ({
            name: x.name,
            val: x.val,
        }));
    }

    return result;
}

function formatValue(
    rowData: StrategyParametersRowData,
    timeFormat: TimeFormat
): ParameterGroupValue {
    const val = rowData.val;
    return typeof val === 'string'
        ? replaceUnderscores(val)
        : rowData.name.toUpperCase().includes('_TIME') &&
          typeof val === 'number'
        ? safeConvertDate(val, timeFormat)
        : val;
}

function replaceUnderscores(text: string): string {
    return text.replace(/_/g, ' ');
}

function safeConvertDate(val: number, timeFormat: TimeFormat): string {
    try {
        return timeFormat.toDateTime(val);
    } catch {
        return val.toString();
    }
}
